import { useQueryClient } from "@tanstack/react-query";
import { SyntheticEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { _3_DAYS } from "../../../../constants/durations";
import { useSpace } from "../../../../hooks/useSpace";
import { useSpacePath } from "../../../../hooks/useSpacePath";
import { keys as remindersKeys } from "../../../reminders/api/keys";
import { fetchReminders } from "../../../reminders/api/useGetReminders";
import { useGetCommunicationsCount } from "../../../topics/api/useGetCommunicationsCount";
import { keys as providersKeys } from "../../api/keys";
import useGetProviderContracts, { fetchProviderContracts } from "../../api/useGetProviderContracts";
import useGetProviderContractsCount from "../../api/useGetProviderContractsCount";
import useGetProviderDocumentsCount from "../../api/useGetProviderDocumentsCount";
import useGetProviderReminders from "../../api/useGetProviderReminders";
import useGetProviderRemindersCount from "../../api/useGetProviderRemindersCount";

export const PROVIDER_TABS = ['contracts', 'reminders', 'documents', 'communications', 'historic'] as const;

export type ProviderTab = typeof PROVIDER_TABS[number];

export function isProviderTab(value: string | undefined): value is ProviderTab {
  return PROVIDER_TABS.some((tab) => tab === value);
}

export function getProviderTabFromPathname(pathname: string): ProviderTab {
  const lastSegment = pathname.replace(/\/+$/, '').split('/').pop();
  return isProviderTab(lastSegment) ? lastSegment : 'contracts';
}

export function getProviderTabUrl(
  spacePath: (path: string) => string,
  providerId: string,
  tab: ProviderTab
): string {
  return spacePath(`/providers/${providerId}/${tab}`);
}

type Props = {
  providerId: string;
};

export function useProviderDetails(props: Props) {
  const navigate = useNavigate();
  const spacePath = useSpacePath();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const { currentSpace } = useSpace();

  const currentTab: ProviderTab = getProviderTabFromPathname(pathname);

  // Lazy data: only the active tab fetches its full payload upfront.
  useGetProviderContracts(props.providerId, currentTab === 'contracts');
  useGetProviderReminders(props.providerId, false, currentTab === 'reminders');

  // Lightweight count queries for tab labels (HEAD requests, no payload).
  const { data: contractsCount } = useGetProviderContractsCount(props.providerId);
  const { data: remindersCount } = useGetProviderRemindersCount(props.providerId);
  const { data: documentsCount } = useGetProviderDocumentsCount(props.providerId);
  const { data: communicationsCount } = useGetCommunicationsCount({ providerId: props.providerId });

  function handleTabChange(_event: SyntheticEvent, tab: ProviderTab) {
    navigate(getProviderTabUrl(spacePath, props.providerId, tab));
  }

  function handlePrefetch(tab: ProviderTab) {
    if (tab === 'contracts') {
      queryClient.prefetchQuery({
        queryKey: providersKeys.getProviderContracts(props.providerId),
        queryFn: () => fetchProviderContracts(props.providerId),
        staleTime: _3_DAYS,
      });
      return;
    }

    if (tab === 'reminders' && currentSpace?.id) {
      const params = { provider_id: props.providerId, is_completed: false };
      queryClient.prefetchQuery({
        queryKey: remindersKeys.list(params),
        queryFn: () => fetchReminders(params, currentSpace.id),
        staleTime: _3_DAYS,
      });
    }
  }

  return {
    currentTab,
    counts: {
      contracts: contractsCount,
      reminders: remindersCount,
      documents: documentsCount,
      communications: communicationsCount,
    },
    handleTabChange,
    handlePrefetch,
  };
}
